import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Share2, Navigation, Route, Star, ChevronDown,
  MapPin, Car, Tent, Waves, TreePine, ExternalLink, Minimize2, Map as MapIcon,
} from 'lucide-react';
import { CampingSpot, Coordinates } from '../../types';
import SpotPreviewOverlay from './SpotPreviewOverlay';

type OverlayState = 'compact' | 'expanded';

interface FollowSpotOverlayProps {
  spot: CampingSpot;
  distanceM: number | null;
  isUserOverride?: boolean;
  position?: 'desktop' | 'mobile';
  onNavigateExternal: (lat: number, lon: number, spot?: CampingSpot) => void;
  onCreateRoute?: (spot: CampingSpot) => void;
  onShare?: (spot: CampingSpot) => void;
  onSelectSpot: (id: string) => void;
  userPosition?: Coordinates | null;
}

// --- Helpers ---

/** Build an ESRI satellite tile URL for a given lat/lon at a zoom level. */
function getEsriTileUrl(lat: number, lon: number, zoom = 16): string {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lon + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`;
}

/** Compose a 2×1 grid of ESRI tiles to give wider coverage. */
function getSatelliteTileUrls(lat: number, lon: number, zoom = 16): string[] {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lon + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return [
    `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`,
    `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x + 1}`,
  ];
}

function formatDistance(m: number | null): string {
  if (m == null) return '';
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function getSpotIcon(spot: CampingSpot, size = 14) {
  const type = spot.spotType || '';
  if (type === 'rasteplass' || type === 'snuplass') return <Car size={size} className="text-blue-400" />;
  if (type === 'commercial_campsite') return <Tent size={size} className="text-amber-400" />;
  if (type === 'coastal') return <Waves size={size} className="text-cyan-400" />;
  if (type === 'gapahuk') return <TreePine size={size} className="text-emerald-400" />;
  return <MapPin size={size} className="text-emerald-400" />;
}

function getLegalStatus(spot: CampingSpot): { label: string; className: string } {
  const type = spot.spotType || '';
  if (type === 'commercial_campsite') return { label: 'Campingplass', className: 'text-amber-500 bg-amber-500/10 border-amber-500/20' };
  if (type === 'rasteplass' || type === 'snuplass') return { label: 'Offentlig', className: 'text-blue-500 bg-blue-500/10 border-blue-500/20' };
  const status = spot.aiResult?.anbefaling || 'SJEKK_SKILT';
  if (status === 'LOVLIG') return { label: 'Fri tilgang', className: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' };
  if (status === 'FORBUDT') return { label: 'Forbudt', className: 'text-red-500 bg-red-500/10 border-red-500/20' };
  return { label: 'Sjekk skilt', className: 'text-amber-500 bg-amber-500/10 border-amber-500/20' };
}

const FollowSpotOverlay: React.FC<FollowSpotOverlayProps> = ({
  spot,
  distanceM,
  isUserOverride = false,
  position = 'desktop',
  onNavigateExternal,
  onCreateRoute,
  onShare,
  onSelectSpot,
  userPosition,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<OverlayState>(position === 'mobile' ? 'compact' : 'expanded');
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [deadPhotos, setDeadPhotos] = useState<Set<string>>(new Set());
  const carouselTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);


  // Reset state on spot change
  useEffect(() => {
    setState(position === 'mobile' ? 'compact' : 'expanded');
    setCarouselIdx(0);
    setThumbFailed(false);
    setDeadPhotos(new Set());
  }, [spot.id, position]);

  // Satellite tile URLs (ESRI — always available)
  const satelliteTiles = useMemo(() => {
    if (!spot.location) return [];
    return getSatelliteTileUrls(spot.location.latitude, spot.location.longitude, 16);
  }, [spot.location?.latitude, spot.location?.longitude]);

  // Single satellite tile for compact thumbnail
  const satelliteThumbUrl = useMemo(() => {
    if (!spot.location) return '';
    return getEsriTileUrl(spot.location.latitude, spot.location.longitude, 15);
  }, [spot.location?.latitude, spot.location?.longitude]);

  // Compact thumbnail — prefer user photo, fallback satellite
  const preferredThumb = useMemo(() => {
    if (spot.photoUrls?.length) return spot.photoUrls[0];
    if (spot.auto_image_url) return spot.auto_image_url;
    return null;
  }, [spot.id, spot.photoUrls, spot.auto_image_url]);
  const thumbUrl = (!thumbFailed && preferredThumb) || satelliteThumbUrl;

  // Carousel images: user-uploaded and auto_image (skip dead URLs)
  const carouselPhotos = useMemo(() => {
    const urls: string[] = [];
    if (spot.photoUrls?.length) urls.push(...spot.photoUrls);
    if (spot.auto_image_url && !urls.includes(spot.auto_image_url)) urls.push(spot.auto_image_url);
    return urls.filter((u) => !deadPhotos.has(u));
  }, [spot.id, spot.photoUrls, spot.auto_image_url, deadPhotos]);

  // Keep carousel index in range after photos die
  useEffect(() => {
    if (carouselPhotos.length === 0) return;
    if (carouselIdx >= carouselPhotos.length) setCarouselIdx(0);
  }, [carouselPhotos.length, carouselIdx]);

  // Auto-scroll carousel
  useEffect(() => {
    if (carouselTimerRef.current) clearInterval(carouselTimerRef.current);
    if (state !== 'expanded' || carouselPhotos.length <= 1) return;
    carouselTimerRef.current = setInterval(() => {
      setCarouselIdx((prev) => (prev + 1) % carouselPhotos.length);
    }, 4000);
    return () => { if (carouselTimerRef.current) clearInterval(carouselTimerRef.current); };
  }, [state, carouselPhotos.length]);

  const legal = getLegalStatus(spot);
  const distLabel = formatDistance(distanceM);

  const toggleState = useCallback(() => {
    setState((prev) => (prev === 'compact' ? 'expanded' : 'compact'));
  }, []);

  return (
    <div
      key={spot.id}
      className={`absolute z-[2500] pointer-events-auto ${
        position === 'mobile'
          ? 'bottom-20 left-4 right-4'
          : 'top-[96px] left-[72px]'
      }`}
      style={position === 'mobile' ? undefined : { width: 'min(340px, calc(100vw - 120px))' }}
    >
      <div className="bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-2xl shadow-2xl overflow-hidden">

        {/* === Expanded view (default) === */}
        {state === 'expanded' && (
          <>
            {/* Satellite tiles with overlaid spot info */}
            <div className="relative w-full h-40 bg-stone-800 overflow-hidden">
              <div className="flex w-[200%] h-full" style={{ marginLeft: '-25%' }}>
                {satelliteTiles.map((url, i) => (
                  <img key={i} src={url} alt="" className="h-full flex-1 object-cover" loading="eager" />
                ))}
              </div>
              {/* Gradient overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

              {/* Minimize button */}
              <button
                onClick={(e) => { e.stopPropagation(); setState('compact'); }}
                className="absolute top-2 right-2 w-7 h-7 rounded-lg bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-colors"
              >
                <Minimize2 size={13} />
              </button>

              {/* Top-left: legal status badge */}
              <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-black/60">
                {getSpotIcon(spot, 12)}
                <span className={`text-3xs font-bold px-1.5 py-0.5 rounded border ${legal.className}`}>
                  {legal.label}
                </span>
              </div>

              {/* User override indicator */}
              {isUserOverride && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-blue-500/90 text-4xs font-bold text-white uppercase tracking-wider">
                  {t("auto.map.followspotoverlay.valgt", "Valgt")}
                </div>
              )}

              {/* Bottom overlay: name, rating, distance */}
              <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5 pt-6">
                <div className="flex items-center gap-1.5">
                  {getSpotIcon(spot, 13)}
                  <span className="text-[14px] font-black text-white truncate flex-1 drop-shadow-lg">
                    {spot.name || 'Ukjent plass'}
                  </span>
                  {spot.isVerified && <Star size={12} className="text-amber-400 shrink-0" fill="currentColor" />}
                </div>
                <div className="flex items-center gap-2.5 mt-0.5">
                  {(spot.score ?? 0) > 0 && (
                    <span className="flex items-center gap-0.5 text-tiny font-bold text-amber-400 drop-shadow">
                      <Star size={10} fill="currentColor" /> {spot.score?.toFixed(1)}
                    </span>
                  )}
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: spot.suitabilityColor || '#22c55e' }}
                  />
                  {distLabel && (
                    <span className="text-tiny font-semibold text-white/80 drop-shadow">{distLabel}</span>
                  )}
                  {(spot.reviewCount ?? 0) > 0 && (
                    <span className="flex items-center gap-0.5 text-2xs font-semibold text-white/70 drop-shadow">
                      <Star size={8} className="text-amber-400" /> {spot.reviewCount}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* User photos carousel (if available) */}
            {carouselPhotos.length > 0 && (
              <div className="relative w-full h-32 bg-stone-200 overflow-hidden">
                <img
                  src={carouselPhotos[carouselIdx] || carouselPhotos[0]}
                  alt=""
                  className="w-full h-full object-cover transition-opacity duration-500"
                  loading="lazy"
                  onError={() => {
                    const url = carouselPhotos[carouselIdx] || carouselPhotos[0];
                    if (url) setDeadPhotos((prev) => new Set(prev).add(url));
                  }}
                />
                {carouselPhotos.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCarouselIdx((i) => (i - 1 + carouselPhotos.length) % carouselPhotos.length); }}
                      className="absolute left-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/50 text-white text-xs flex items-center justify-center"
                    >
                      &lsaquo;
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCarouselIdx((i) => (i + 1) % carouselPhotos.length); }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/50 text-white text-xs flex items-center justify-center"
                    >
                      &rsaquo;
                    </button>
                    <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 px-2 py-0.5 bg-black/50 rounded-full">
                      {carouselPhotos.map((_, i) => (
                        <button
                          key={i}
                          onClick={(e) => { e.stopPropagation(); setCarouselIdx(i); }}
                          className={`w-1.5 h-1.5 rounded-full transition-all ${i === carouselIdx ? 'bg-white w-3' : 'bg-white/40'}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* AI assessment — only if available, compact */}
            {spot.aiResult?.vurdering && (
              <div className="px-3 py-2 border-b border-[var(--border-primary)]">
                <p className="text-2xs text-[var(--text-secondary)] leading-relaxed line-clamp-2">
                  {spot.aiResult.vurdering}
                </p>
              </div>
            )}

            {/* Action buttons — single compact row */}
            <div className="px-3 py-2">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={(e) => { e.stopPropagation(); onNavigateExternal(spot.location.latitude, spot.location.longitude, spot); }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-2xs font-black uppercase tracking-widest transition-all active:scale-95"
                >
                  <Navigation size={12} /> {t("auto.map.followspotoverlay.naviger", "Naviger")}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onSelectSpot(spot.id); }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-primary)] text-[var(--text-primary)] text-2xs font-black uppercase tracking-widest transition-all active:scale-95"
                >
                  <ExternalLink size={12} /> {t("auto.map.followspotoverlay.detaljer", "Detaljer")}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMapPreview(true); }}
                  className="w-9 h-9 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 flex items-center justify-center text-amber-400 transition-all active:scale-95"
                  title={t("auto.map.followspotoverlay.title.vis_i_satellittkart", "Vis i satellittkart")}
                >
                  <MapIcon size={13} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onShare?.(spot); }}
                  className="w-9 h-9 rounded-xl bg-[var(--bg-surface-alt)] flex items-center justify-center text-[var(--text-muted)] hover:text-emerald-400 transition-all active:scale-95"
                >
                  <Share2 size={13} />
                </button>
                {onCreateRoute && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onCreateRoute(spot); }}
                    className="w-9 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-all active:scale-95"
                    title={t("auto.map.followspotoverlay.title.lag_rute", "Lag rute")}
                  >
                    <Route size={13} />
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* === Compact view === */}
        {state === 'compact' && (
          <div className="flex items-center gap-2 px-3 py-2.5">
            <div
              className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer hover:bg-[var(--bg-surface-alt)]/50 rounded-lg transition-colors"
              onClick={toggleState}
            >
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-stone-200 shrink-0">
                <img
                  src={thumbUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={() => {
                    if (preferredThumb && !thumbFailed) setThumbFailed(true);
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {getSpotIcon(spot, 12)}
                  <span className="text-[12px] font-black text-[var(--text-primary)] truncate">
                    {spot.name || 'Ukjent plass'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-3xs font-bold px-1.5 py-0.5 rounded-md border ${legal.className}`}>
                    {legal.label}
                  </span>
                  {distLabel && (
                    <span className="text-2xs font-semibold text-[var(--text-secondary)]">{distLabel}</span>
                  )}
                </div>
              </div>
              <ChevronDown size={14} className="shrink-0 text-[var(--text-muted)]" />
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); onSelectSpot(spot.id); }}
                className="px-2.5 py-2 rounded-xl bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-primary)] text-[var(--text-primary)] text-3xs font-black uppercase tracking-widest transition-all active:scale-95"
              >
                {t("auto.map.followspotoverlay.detaljer", "Detaljer")}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onNavigateExternal(spot.location.latitude, spot.location.longitude, spot); }}
                className="px-2.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-3xs font-black uppercase tracking-widest transition-all active:scale-95"
              >
                <Navigation size={12} />
              </button>
            </div>
          </div>
        )}

      </div>

      {showMapPreview && (
        <SpotPreviewOverlay
          spot={spot}
          distanceM={distanceM ?? undefined}
          onClose={() => setShowMapPreview(false)}
          onDetails={(id) => { setShowMapPreview(false); onSelectSpot(id); }}
          userPosition={userPosition}
        />
      )}
    </div>
  );
};

export default FollowSpotOverlay;
